import React from 'react';
import * as Sentry from '@sentry/react';
import * as Styled from './style';
import { debug } from '../../services/logger';

/**
 * ErrorBoundary is a reusable React component for catching errors in child components.
 *
 * @component
 * @example
 * return (
 *   <ErrorBoundary componentName="UserProfile">
 *     <UserProfile />
 *   </ErrorBoundary>
 * )
 *
 * @typedef {Object} ErrorBoundaryProps
 * @property {string} componentName - The name of the component being wrapped. Used for logging and error reporting.
 * @property {React.ReactNode} children - Child components to render.
 * @property {React.ReactNode} [fallback] - Optional fallback UI to render on error.
 */

interface ErrorBoundaryProps {
  componentName: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const { componentName } = this.props;
    const namedError = new Error(`[Fatal]: ${error.message}`);
    namedError.stack = error.stack;

    debug('ErrorBoundary', `fatal render error at ${componentName}`, { error, errorInfo });

    Sentry.captureException(namedError, {
      tags: { component: componentName },
      extra: {
        componentStack: errorInfo.componentStack,
        ...errorInfo
      }
    });
  }

  render(): React.ReactNode {
    const { hasError, error } = this.state;
    const { fallback, componentName, children } = this.props;

    if (hasError) {
      return fallback ? (
        fallback
      ) : (
        <Styled.ErrorContainer>
          <h2>
            Something went wrong in <strong>{componentName}</strong>.
          </h2>
          <pre>{error?.toString()}</pre>
        </Styled.ErrorContainer>
      );
    }

    return children;
  }
}

export default ErrorBoundary;
